import React, { useState } from 'react';
import {
    Card,
    Tag,
    Button,
    Select,
    message,
    Space,
    Tooltip,
    Modal,
    Badge,
    Divider
} from 'antd';
import {
    RightOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    ClockCircleOutlined,
    PhoneOutlined,
    DollarOutlined,
    CarOutlined,
    CloseCircleOutlined,
    WarningOutlined
} from '@ant-design/icons';

const { Option } = Select;

const stageTransitionRules = {
    "New": [
        "Call Back",
        "Error",
        "Fake",
        "Follow-Up",
        "Hang Up",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Test Lead",
        "Voice Mail",
        "Wrong Number"
    ],
    "Call Back": [
        "Follow-Up",
        "Hang Up",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Voice Mail",
        "Wrong Number"
    ],
    "Voice Mail": [
        "Call Back",
        "Follow-Up",
        "Hang Up",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Wrong Number"
    ],
    "Follow-Up": [
        "Hang Up",
        "Negotiation",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Sales Made",
        "Wrong Number"
    ],
    "Quoted": [
        "Negotiation",
        "Not Interested",
        "Part Not Available",
        "Follow-Up",
        "Payment Completed",
        "Pending Payment",
        "Sales Made"
    ],
    "Negotiation": [
        "Not Interested",
        "Part Not Available",
        "Payment Completed",
        "Pending Payment",
        "Sales Made"
    ],
    "Pending Payment": [
        "Negotiation",
        "Payment Completed",
        "Quoted",
        "Sales Made"
    ],
    "Payment Completed": [
        "Sales Made"
    ],
    "Sales Made": [],
    "Part Not Available": [
        "Call Back",
        "Follow-Up",
        "New"
    ],
    "Wrong Number": [],
    "Not Interested": [],
    "Hang Up": [],
    "Fake": [],
    "Test Lead": [],
    "Error": [
        "Call Back",
        "Error",
        "Fake",
        "Follow-Up",
        "Hang Up",
        "New",
        "Not Interested",
        "Part Not Available",
        "Quoted",
        "Test Lead",
        "Voice Mail",
        "Wrong Number"
    ]
};

// Stage colors and icons mapping
const stageConfig = {
    "New": { color: "#1890ff", icon: <ClockCircleOutlined />, bgColor: "#e6f7ff" },
    "Call Back": { color: "#722ed1", icon: <PhoneOutlined />, bgColor: "#f9f0ff" },
    "Voice Mail": { color: "#13c2c2", icon: <PhoneOutlined />, bgColor: "#e6fffb" },
    "Follow-Up": { color: "#fa8c16", icon: <ClockCircleOutlined />, bgColor: "#fff7e6" },
    "Quoted": { color: "#52c41a", icon: <DollarOutlined />, bgColor: "#f6ffed" },
    "Negotiation": { color: "#faad14", icon: <ExclamationCircleOutlined />, bgColor: "#fffbe6" },
    "Pending Payment": { color: "#fadb14", icon: <ClockCircleOutlined />, bgColor: "#feffe6" },
    "Payment Completed": { color: "#a0d911", icon: <CheckCircleOutlined />, bgColor: "#fcffe6" },
    "Sales Made": { color: "#389e0d", icon: <CheckCircleOutlined />, bgColor: "#f6ffed" },
    "Part Not Available": { color: "#ff7875", icon: <CarOutlined />, bgColor: "#fff2e8" },
    "Wrong Number": { color: "#9e9e9e", icon: <CloseCircleOutlined />, bgColor: "#f5f5f5" },
    "Not Interested": { color: "#9e9e9e", icon: <CloseCircleOutlined />, bgColor: "#f5f5f5" },
    "Hang Up": { color: "#ff4d4f", icon: <CloseCircleOutlined />, bgColor: "#fff2e8" },
    "Fake": { color: "#ff4d4f", icon: <WarningOutlined />, bgColor: "#fff2e8" },
    "Test Lead": { color: "#bfbfbf", icon: <WarningOutlined />, bgColor: "#fafafa" },
    "Error": { color: "#ff4d4f", icon: <ExclamationCircleOutlined />, bgColor: "#fff2e8" }
};

// Stage categories for grouping
const stageCategories = {
    "Active": ["New", "Call Back", "Voice Mail", "Follow-Up", "Quoted", "Negotiation", "Pending Payment"],
    "Successful": ["Payment Completed", "Sales Made"],
    "Unsuccessful": ["Not Interested", "Hang Up", "Wrong Number", "Fake", "Test Lead"],
    "Issues": ["Part Not Available", "Error"]
};

const LeadStageCard = ({ currentStage, leadDetails }) => {
    const [newStage, setNewStage] = useState('');
    const [loading, setLoading] = useState(false);

    // Map stage names for display
    const getDisplayStageName = (stage) => {
        if (stage === "Payment Completed") {
            return "Payment Details Received";
        }
        return stage;
    };

    const currentStageConfig = stageConfig[currentStage] || stageConfig["New"];
    const availableTransitions = stageTransitionRules[currentStage] || [];
    
    // Get display stage name
    const displayStageName = getDisplayStageName(currentStage);

    const handleStageChange = async () => {
        if (!newStage) {
            message.warning('Please select a new stage');
            return;
        }

        setLoading(true);
        try {
            // Here you would call your API to update the lead stage
            // await updateLeadStage(leadDetails.id, newStage);

            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 800));

            message.success(`Lead stage updated to ${newStage}`);
            setNewStage('');
        } catch (error) {
            message.error('Failed to update lead stage');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const getStageCategory = (stage) => {
        for (const [category, stages] of Object.entries(stageCategories)) {
            if (stages.includes(stage)) {
                return category;
            }
        }
        return "Other";
    };

    // Group available transitions by category
    const groupedTransitions = availableTransitions.reduce((groups, stage) => {
        const category = getStageCategory(stage);
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(stage);
        return groups;
    }, {});

    return (
        <Card
            style={{
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                border: `2px solid ${currentStageConfig.color}20`,
                background: 'white'
            }}
        >
            {/* Current Stage Display */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: "5px",
                padding: '16px',
                backgroundColor: currentStageConfig.bgColor,
                borderRadius: '8px',
                borderLeft: `4px solid ${currentStageConfig.color}`
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: currentStageConfig.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '18px'
                    }}>
                        {currentStageConfig.icon}
                    </div>
                    <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                            CURRENT STAGE
                        </div>
                        <div style={{
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: currentStageConfig.color
                        }}>
                            {displayStageName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', marginTop: 2 }}>
                            {getStageCategory(currentStage)} • Updated: Today
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default LeadStageCard;


